/**
 * Editor Store - 编辑器 UI 状态（临时）
 *
 * 职责：管理编辑器的 UI 交互状态
 * - 当前编辑的模板 ID
 * - 当前编辑的 SKU/Selection
 * - 当前编辑的 Toning/ColorSet
 * - 预览面板状态
 * - ZIP 版本 指纹
 */
import * as spine from "@esotericsoftware/spine-webgl";
import { create } from "zustand";
import { useConfigStore } from "./configStore";
import { randomizeSkins } from "../lib/randomizer";
import { generateSkuId } from "../lib/spineParser";
import { resolveParsedSkin } from "./skinResolve";
import {
  getSelectionRequireCategories,
  isLayerIconRequired,
  selectionOptFromLayerIcon,
  syncRequireCategoriesForSelection,
} from "../lib/selectionUtils";
import { SKU } from "../types/sku";
import type { SpinePreviewApp } from "../lib/SpinePreviewApp";
import type { StaticStackItem } from "../lib/staticLayerStack";

/** 关联模版在列表里用的 composite selectionId：`${relatedTemplateId}_${originalSelectionId}` */
type RelatedTemplateForToggle = {
  id?: string;
  config?: { selection?: any[]; skus?: Record<string, any> };
};

function getSkuIdsForCompositeSelection(
  selectionId: string,
  loadedRelatedTemplates: RelatedTemplateForToggle[] | undefined,
): string[] {
  for (const rel of loadedRelatedTemplates || []) {
    for (const sel of rel.config?.selection || []) {
      if (`${rel.id}_${sel.id}` === selectionId) {
        return sel.skuIds || [];
      }
    }
  }
  return [];
}

function isRelatedCompositeSelectionId(
  selectionId: string | undefined,
  loadedRelatedTemplates: RelatedTemplateForToggle[] | undefined,
): boolean {
  if (!selectionId) return false;
  return getSkuIdsForCompositeSelection(selectionId, loadedRelatedTemplates).length > 0;
}

function getSkinNamesForCompositeSelection(
  selectionId: string,
  loadedRelatedTemplates: RelatedTemplateForToggle[] | undefined,
): Set<string> {
  const ids = getSkuIdsForCompositeSelection(selectionId, loadedRelatedTemplates);
  const names = new Set<string>();
  for (const rel of loadedRelatedTemplates || []) {
    const skus = rel.config?.skus || {};
    for (const id of ids) {
      const skinName = skus[id]?.skinName;
      if (skinName) names.add(skinName);
    }
  }
  return names;
}

// SKU 错误类型
export interface SkuError {
  type: "orphan" | "invalid_toning" | "duplicate_toning";
  message: string;
  details?: string[];
}

// ColorSet 错误类型
export interface ColorSetError {
  type: "missing_light_dark";
  message: string;
}

// ============ Store 定义 ============

interface EditorStore {
  // 是否有未保存的改动
  isDirty: boolean;
  setIsDirty: (value: boolean) => void;

  // 当前激活的 Tab
  activeTab: string;
  setActiveTab: (tab: string) => void;

  // 当前编辑的 SKU（用于右侧详情面板）
  editingSkuId: string | null;
  setEditingSku: (skuId: string | null, selectionId: string | null) => void;

  // 当前编辑的 Selection（用于右侧详情面板）
  editingSelectionId: string | null;
  setEditingSelectionId: (selectionId: string | null) => void;

  // 当前编辑的 Toning（用于右侧详情面板）
  editingToningId: string | null;
  setEditingToningId: (toningId: string | null) => void;

  editingColorSetId: string | null;
  setEditingColorSetId: (
    toningId: string | null,
    colorSetId: string | null,
  ) => void;

  // 当前预览的 Skin（用于显示未添加到 Selection 的 skin 详情）
  previewSkinName: string | null;
  setPreviewSkinName: (skinName: string | null) => void;

  // ============ Preview State (from ConfigStore) ============

  // 当前预览的 skinNames（选中的 SKU 对应的 skin）
  selectedSkins: string[];
  setSelectedSkins: (skins: string[]) => void;
  toggleSkin: (skinName: string, selectionId?: string) => void;

  // 当前播放的动画
  currentAnimation: string;
  setCurrentAnimation: (animation: string) => void;

  // 当前选中的染色方案（toningId -> colorId）
  selectedToningColors: Record<string, string>;
  setToningColor: (toningId: string, colorId: string) => void;
  clearToningColor: (toningId: string) => void;
  /** 整表覆盖，用于还原套装，避免上一套残留染色抢先生效 */
  setSelectedToningColors: (colors: Record<string, string>) => void;

  // 当前预览的染色方案 ID（用于多染色绑定时确定优先显示哪个）
  previewToningId: string | null;
  setPreviewToningId: (toningId: string | null) => void;

  // 随机换装
  randomizeSkins: () => void;

  // ============ ZIP State ============

  // ZIP
  existingZipUrl: string | null;
  setExistingZipUrl: (url: string | null) => void;

  existingFingerprint: string | null;
  setExistingFingerprint: (md5: string | null) => void;

  existingSpineVersion: number | null;
  setExistingSpineVersion: (version: number | null) => void;

  /**
   * Spine 源文件是否在本会话被重新导入。
   * false：保存时复用已有 Zip URL（可与当前 tid 不一致，如子模版共用父 Zip）
   * true：保存时按内容决定是否重新上传 Zip
   */
  spineZipDirty: boolean;
  setSpineZipDirty: (dirty: boolean) => void;

  // Spine 预览实例引用
  spineApp: SpinePreviewApp | null;
  spineCanvas: spine.SpineCanvas | null;
  setSpinePreview: (app: SpinePreviewApp | null, canvas: spine.SpineCanvas | null) => void;

  /** 头像编辑器：人物下 / 人物上 两路 Spine 预览（导出复用） */
  avatarSpineBelowApp: SpinePreviewApp | null;
  avatarSpineBelowCanvas: spine.SpineCanvas | null;
  avatarSpineAboveApp: SpinePreviewApp | null;
  avatarSpineAboveCanvas: spine.SpineCanvas | null;
  setAvatarSpinePreviewSlots: (slots: {
    below: { app: SpinePreviewApp; canvas: spine.SpineCanvas } | null;
    above: { app: SpinePreviewApp; canvas: spine.SpineCanvas } | null;
  }) => void;

  /**
   * 与 {@link SpinePreview} 中 mergedStaticStack 一致，由预览面板写入。
   * 导出动图合成静态层时直接读此字段，避免在导出侧重复拼叠层逻辑。
   */
  previewMergedStaticStack: StaticStackItem[];
  setPreviewMergedStaticStack: (items: StaticStackItem[]) => void;

  // 重置预览状态
  resetPreview: () => void;

  // ============ Actions ============

  // Selection 操作
  createSelection: (id: string, name: string) => void;
  deleteSelection: (id: string) => void;
  updateSelectionName: (id: string, name: string) => void;
  updateSelectionLayerIcon: (
    id: string,
    icon: { id: string; gid?: string; opt?: boolean },
  ) => void;
  /** 按 Selection 内 SKU 的 category 增删 requireCategories（不改 Selection 结构） */
  setSelectionRequired: (id: string, required: boolean) => boolean;
  updateSelectionGid: (id: string, gid: string) => void;
  updateSelectionBackground: (id: string, background: number) => void;
  updateSelectionPlayIndex: (id: string, playIndex: number) => void;
  updateSelectionHidden: (id: string, hidden: boolean) => void;
  updateSelectionOrder: (selections: any[]) => void;
  moveSkuToSelection: (
    skuId: string,
    fromSelectionId: string,
    toSelectionId: string,
  ) => void;
  mergeSelectionInto: (fromSelectionId: string, toSelectionId: string) => void;

  // SKU 操作（SKU 配置独立于 Selection）
  getSkuConfig: (skinName: string) => any; // 获取 SKU 配置
  getSkuErrors: (skuId: string) => SkuError[]; // 获取 SKU 的所有错误
  cleanSkuToning: (skuId: string) => void; // 清理 SKU 的无效和重复 toning 绑定
  updateSkuPro: (skinName: string, pro: number) => void;
  updateSkuHidden: (skinName: string, hidden: boolean) => void;
  addSkuToning: (skinName: string, toningId: string) => void;
  removeSkuToning: (skinName: string, toningId: string) => void;
  setSkuTonings: (skinName: string, toningIds: string[]) => void;

  // Selection 管理（只管理 SKU 的组织）
  addSkuToSelection: (selectionId: string, skinName: string) => void;
  addMultipleSkusToSelection: (
    selectionId: string,
    skinNames: string[],
  ) => void;
  /** 静态模版：新建空 SKU（一般不再走 UI，优先用带图创建） */
  addStaticSkuToSelection: (selectionId: string) => string | undefined;
  /** 静态模版：上传拿到资源 ID 后，一次性新建带原图/缩略图的 SKU */
  addStaticSkuToSelectionWithAssets: (
    selectionId: string,
    assets: { originalAssetId: string; thumbAssetId: string },
  ) => string | undefined;
  updateStaticSkuField: (
    skuId: string,
    patch: Partial<SKU> & {
      originalAssetId?: string;
      thumbAssetId?: string;
    },
  ) => void;
  /** 任意 SKU：按 skuId 更新字段（含动态 SKU 的 thumbAssetId） */
  updateSkuField: (
    skuId: string,
    patch: Partial<SKU> & {
      originalAssetId?: string | null;
      thumbAssetId?: string | null;
    },
  ) => void;
  removeSkuFromSelection: (selectionId: string, skuId: string) => void;
  /** 静态 SKU：从分组移除并删除未引用的 SKU 数据 */
  removeStaticSkuFromSelection: (selectionId: string, skuId: string) => void;
  removeOrphanSkus: () => number;
  // 批量操作：为多个 SKU 绑定/解绑 Toning
  bindToningToSkus: (
    toningId: string,
    skuIds: { selectionId: string; skuId: string }[],
  ) => void;
  unbindToningFromSkus: (
    toningId: string,
    skuIds: { selectionId: string; skuId: string }[],
  ) => void;
  reorderSkuInSelection: (
    selectionId: string,
    oldIndex: number,
    newIndex: number,
  ) => void;

  // Toning 操作
  addToning: (toning: any) => void;
  updateToning: (toningId: string, updates: Partial<any>) => void;
  deleteToning: (toningId: string) => void;
  addColorSet: (toningId: string, colorSet: any) => void;
  updateColorSet: (
    toningId: string,
    colorSetId: string,
    updates: {
      light?: string;
      dark?: string;
      colors?: any[];
      requireAd?: number;
    },
  ) => void;
  deleteColorSet: (toningId: string, colorSetId: string) => void;
  moveColorSet: (
    fromToningId: string,
    toToningId: string,
    colorSetId: string,
  ) => void;

  // Animation 操作
  updateAnimationPro: (animationId: string, pro: number) => void;
  updateAnimationLoop: (animationId: string, loop: boolean) => void;

  // RequireSlots 操作（随机必选的 slot）
  toggleRequireCategory: (category: string) => void;
}

export const useEditorStore = create<EditorStore>((set, get) => ({
  isDirty: false,
  activeTab: "info",
  editingSkuId: null,
  editingSelectionId: null,
  editingToningId: null,
  editingColorSetId: null,
  previewSkinName: null,

  // Preview State
  selectedSkins: [],
  currentAnimation: "",
  selectedToningColors: {},
  previewToningId: null,

  spineApp: null,
  spineCanvas: null,

  avatarSpineBelowApp: null,
  avatarSpineBelowCanvas: null,
  avatarSpineAboveApp: null,
  avatarSpineAboveCanvas: null,

  previewMergedStaticStack: [],

  existingZipUrl: null,
  existingFingerprint: null,
  existingSpineVersion: null,
  spineZipDirty: false,

  setIsDirty: (value) => set({ isDirty: value }),

  setActiveTab: (tab) =>
    set({
      activeTab: tab,
      // 切换 tab 时清除其他 tab 的编辑状态
      editingSkuId: null,
      editingToningId: null,
      editingColorSetId: null,
      editingSelectionId: null,
      previewSkinName: null,
    }),

  setEditingSku: (skuId, selectionId) =>
    set({
      editingSkuId: skuId,
      editingToningId: null, // 清除 toning 编辑状态
      previewSkinName: null, // 清除 skin 预览
    }),

  setEditingSelectionId: (selectionId) =>
    set({
      editingSelectionId: selectionId,
      editingSkuId: null,
      editingToningId: null,
      editingColorSetId: null,
      previewSkinName: null,
    }),

  setEditingToningId: (toningId) =>
    set({
      editingToningId: toningId,
      editingColorSetId: null,
      editingSkuId: null, // 清除 SKU 编辑状态
      previewSkinName: null,
    }),

  setEditingColorSetId: (toningId, colorSetId) =>
    set({
      editingToningId: toningId,
      editingColorSetId: colorSetId,
      editingSkuId: null,
      editingSelectionId: null,
    }),

  setPreviewSkinName: (skinName) =>
    set({
      previewSkinName: skinName,
      editingSkuId: null, // 清除 SKU 编辑状态
    }),

  // ============ Preview Methods ============

  setSelectedSkins: (skins) => set({ selectedSkins: skins }),

  toggleSkin: (skinName, selectionId) => {
    const { selectedSkins } = get();
    const { parsedSkins, templateConfig, loadedRelatedTemplates } =
      useConfigStore.getState();

    const getCategory = (name: string) =>
      resolveParsedSkin(
        name,
        templateConfig,
        parsedSkins,
        loadedRelatedTemplates,
      )?.category ?? null;

    const skin = resolveParsedSkin(
      skinName,
      templateConfig,
      parsedSkins,
      loadedRelatedTemplates,
    );

    if (!skin) {
      console.error("[toggleSkin] skin not found:", skinName);
      return;
    }

    const clickedCategory = skin.category;

    const selectionSkinNames =
      selectionId &&
      isRelatedCompositeSelectionId(selectionId, loadedRelatedTemplates)
        ? getSkinNamesForCompositeSelection(selectionId, loadedRelatedTemplates)
        : null;

    const requireCategories = new Set(templateConfig?.requireCategories || []);

    const filtered = selectedSkins.filter((s) => {
      if (s === skinName) return false;
      const otherCategory = getCategory(s);
      if (!otherCategory) return true;
      if (otherCategory !== clickedCategory) return true;
      if (selectionSkinNames) {
        return !selectionSkinNames.has(s);
      }
      return false;
    });

    let newSelected: string[];
    if (selectedSkins.includes(skinName)) {
      if (requireCategories.has(clickedCategory)) {
        const hasOtherInCategory = filtered.some(
          (s) => getCategory(s) === clickedCategory,
        );
        if (!hasOtherInCategory) {
          console.log(
            "[toggleSkin] cannot deselect required category:",
            clickedCategory,
          );
          return;
        }
      }
      newSelected = filtered;
    } else {
      newSelected = [...filtered, skinName];
    }

    set({ selectedSkins: newSelected });
  },

  setCurrentAnimation: (animation) => set({ currentAnimation: animation }),

  setToningColor: (toningId, colorId) =>
    set((state) => ({
      selectedToningColors: {
        ...state.selectedToningColors,
        [toningId]: colorId,
      },
    })),

  clearToningColor: (toningId) =>
    set((state) => {
      const newColors = { ...state.selectedToningColors };
      delete newColors[toningId];
      return {
        selectedToningColors: newColors,
      };
    }),

  setSelectedToningColors: (colors) =>
    set({ selectedToningColors: { ...colors } }),

  setPreviewToningId: (toningId) => set({ previewToningId: toningId }),

  randomizeSkins: () => {
    const { parsedSkins, templateConfig, loadedRelatedTemplates } =
      useConfigStore.getState();
    const result = randomizeSkins({
      parsedSkins,
      templateConfig,
      relatedTemplates: loadedRelatedTemplates,
    });

    set({
      selectedSkins: result.selectedSkins,
      selectedToningColors: result.selectedToningColors,
    });
  },

  setSpinePreview: (app, canvas) => set({ spineApp: app, spineCanvas: canvas }),

  setAvatarSpinePreviewSlots: ({ below, above }) =>
    set({
      avatarSpineBelowApp: below?.app ?? null,
      avatarSpineBelowCanvas: below?.canvas ?? null,
      avatarSpineAboveApp: above?.app ?? null,
      avatarSpineAboveCanvas: above?.canvas ?? null,
      spineApp: below?.app ?? above?.app ?? null,
      spineCanvas: below?.canvas ?? above?.canvas ?? null,
    }),

  setPreviewMergedStaticStack: (items) =>
    set({ previewMergedStaticStack: items }),

  setExistingZipUrl: (url) => set({ existingZipUrl: url }),
  setExistingFingerprint: (md5) => set({ existingFingerprint: md5 }),
  setExistingSpineVersion: (version) => set({ existingSpineVersion: version }),
  setSpineZipDirty: (dirty) => set({ spineZipDirty: dirty }),

  // ============ Actions ============

  createSelection: (id, name) => {
    const { templateConfig } = useConfigStore.getState();
    if (!templateConfig) return;

    if (templateConfig.selection.some((s) => s.id === id)) {
      console.warn(`Selection ID "${id}" 已存在`);
      return;
    }

    const newSelection: any = {
      id,
      name,
      playIndex: templateConfig.selection.length,
      sku: [],
    };

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        selection: [...templateConfig.selection, newSelection],
      },
    });
    set({ isDirty: true });
  },

  deleteSelection: (id) => {
    const { templateConfig } = useConfigStore.getState();
    if (!templateConfig) return;

    const selection = templateConfig.selection.find((s) => s.id === id);
    if (!selection) return;

    const skuIds = selection.skuIds || [];
    if (skuIds.length > 0) {
      console.warn(`Selection "${id}" 还有 SKU，无法删除`);
      return;
    }

    const filtered = templateConfig.selection.filter((s) => s.id !== id);
    const reindexed = filtered.map((sel, idx) => ({
      ...sel,
      playIndex: idx,
    }));

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        selection: reindexed,
      },
    });
    set({ isDirty: true });
  },

  updateSelectionName: (id, name) => {
    const { templateConfig } = useConfigStore.getState();
    if (!templateConfig) return;

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        selection: templateConfig.selection.map((sel) =>
          sel.id === id ? { ...sel, name } : sel,
        ),
      },
    });
    set({ isDirty: true });
  },

  updateSelectionLayerIcon: (id, icon) => {
    const { templateConfig } = useConfigStore.getState();
    if (!templateConfig) return;

    const selection = templateConfig.selection.find((sel) => sel.id === id);
    const nextOpt = selectionOptFromLayerIcon(icon);
    const required = isLayerIconRequired(icon);
    const categories = getSelectionRequireCategories(
      selection,
      templateConfig.skus,
    );
    // 层字段 + requireCategories 联动
    const nextRequireCategories = syncRequireCategoriesForSelection(
      templateConfig.requireCategories,
      categories,
      required,
    );

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        requireCategories: nextRequireCategories,
        selection: templateConfig.selection.map((sel) =>
          sel.id === id
            ? {
                ...sel,
                layerIconId: icon.id,
                opt: nextOpt,
                ...(icon.gid ? { gid: icon.gid } : {}),
              }
            : sel,
        ),
      },
    });
    set({ isDirty: true });
  },

  setSelectionRequired: (id, required) => {
    const { templateConfig } = useConfigStore.getState();
    if (!templateConfig) return false;

    const selection = templateConfig.selection.find((sel) => sel.id === id);
    if (!selection) return false;

    const categories = getSelectionRequireCategories(
      selection,
      templateConfig.skus,
    );

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        // 层必选字段
        selection: templateConfig.selection.map((sel) =>
          sel.id === id ? { ...sel, opt: required ? false : true } : sel,
        ),
        // 联动 Skin 分类必选（假定一类一层）
        requireCategories: syncRequireCategoriesForSelection(
          templateConfig.requireCategories,
          categories,
          required,
        ),
      },
    });
    set({ isDirty: true });
    return true;
  },

  updateSelectionGid: (id, gid) => {
    const { templateConfig } = useConfigStore.getState();
    if (!templateConfig) return;

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        selection: templateConfig.selection.map((sel) =>
          sel.id === id ? { ...sel, gid: gid || undefined } : sel,
        ),
      },
    });
    set({ isDirty: true });
  },

  updateSelectionBackground: (id, background) => {
    const { templateConfig } = useConfigStore.getState();
    if (!templateConfig) return;

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        selection: templateConfig.selection.map((sel) =>
          sel.id === id ? { ...sel, background: background || undefined } : sel,
        ),
      },
    });
    set({ isDirty: true });
  },

  updateSelectionPlayIndex: (id, playIndex) => {
    const { templateConfig } = useConfigStore.getState();
    if (!templateConfig) return;

    const selections = [...templateConfig.selection].sort(
      (a, b) => a.playIndex - b.playIndex,
    );
    const currentIndex = selections.findIndex((s) => s.id === id);
    if (currentIndex === -1) return;

    const targetIndex = Math.max(
      0,
      Math.min(playIndex, selections.length - 1),
    );
    if (currentIndex === targetIndex) return;

    const [removed] = selections.splice(currentIndex, 1);
    selections.splice(targetIndex, 0, removed);

    const reindexed = selections.map((sel, idx) => ({
      ...sel,
      playIndex: idx,
    }));

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        selection: reindexed,
      },
    });
    set({ isDirty: true });
  },

  updateSelectionHidden: (id, hidden) => {
    const { templateConfig } = useConfigStore.getState();
    if (!templateConfig) return;

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        selection: templateConfig.selection.map((sel) =>
          sel.id === id ? { ...sel, hidden: hidden || undefined } : sel,
        ),
      },
    });
    set({ isDirty: true });
  },

  updateSelectionOrder: (selections) => {
    const { templateConfig } = useConfigStore.getState();
    if (!templateConfig) return;

    const updatedSelections = selections.map((sel, idx) => ({
      ...sel,
      playIndex: idx,
    }));

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        selection: updatedSelections,
      },
    });
    set({ isDirty: true });
  },

  moveSkuToSelection: (skuId, fromSelectionId, toSelectionId) => {
    const { templateConfig } = useConfigStore.getState();
    if (!templateConfig) return;

    if (fromSelectionId === toSelectionId) return;

    const fromSelection = templateConfig.selection.find(
      (s) => s.id === fromSelectionId,
    );
    const toSelection = templateConfig.selection.find(
      (s) => s.id === toSelectionId,
    );
    if (!fromSelection || !toSelection) return;

    const fromSkuIds =
      fromSelection.skuIds || fromSelection.sku?.map((s: any) => s.id) || [];
    if (!fromSkuIds.includes(skuId)) return;

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        selection: templateConfig.selection.map((sel) => {
          if (sel.id === fromSelectionId) {
            const currentSkuIds =
              sel.skuIds || sel.sku?.map((s: any) => s.id) || [];
            return {
              ...sel,
              skuIds: currentSkuIds.filter((id: string) => id !== skuId),
            };
          }
          if (sel.id === toSelectionId) {
            const currentSkuIds =
              sel.skuIds || sel.sku?.map((s: any) => s.id) || [];
            return { ...sel, skuIds: [...currentSkuIds, skuId] };
          }
          return sel;
        }),
      },
    });
    set({ isDirty: true });
  },

  mergeSelectionInto: (fromSelectionId, toSelectionId) => {
    const { templateConfig } = useConfigStore.getState();
    if (!templateConfig) return;

    if (fromSelectionId === toSelectionId) return;

    const fromSelection = templateConfig.selection.find(
      (s) => s.id === fromSelectionId,
    );
    if (!fromSelection) return;

    const fromSkuIds =
      fromSelection.skuIds || fromSelection.sku?.map((s: any) => s.id) || [];

    const updatedSelections = templateConfig.selection
      .map((sel) => {
        if (sel.id === toSelectionId) {
          const currentSkuIds =
            sel.skuIds || sel.sku?.map((s: any) => s.id) || [];
          return { ...sel, skuIds: [...currentSkuIds, ...fromSkuIds] };
        }
        return sel;
      })
      .filter((sel) => sel.id !== fromSelectionId);

    const reindexed = updatedSelections.map((sel, idx) => ({
      ...sel,
      playIndex: idx,
    }));

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        selection: reindexed,
      },
    });
    set({ isDirty: true });
  },

  // ============ SKU Actions ============

  getSkuConfig: (skinName) => {
    const { templateConfig, parsedSkins, loadedRelatedTemplates } =
      useConfigStore.getState();
    // Search in main
    let parsedSkin = parsedSkins.find((s) => s.name === skinName);
    if (parsedSkin) {
      const skuId = parsedSkin.skuId;
      const sku = templateConfig?.skus?.[skuId];
      if (!sku) {
        return {
          id: skuId,
          skinName,
          category: parsedSkin.category,
          pro: 0,
        };
      }
      return sku;
    }
    // Search in related
    if (loadedRelatedTemplates) {
      for (const rel of loadedRelatedTemplates) {
        parsedSkin = rel.parsedSkins.find((s) => s.name === skinName);
        if (parsedSkin) {
          const skuId = parsedSkin.skuId;
          const sku = rel.config.skus?.[skuId];
          if (!sku) {
            return {
              id: skuId,
              skinName,
              category: parsedSkin.category,
              pro: 0,
            };
          }
          return sku;
        }
      }
    }
    return { pro: 0, skinName };
  },

  getSkuErrors: (skuId) => {
    const { templateConfig, parsedSkins } = useConfigStore.getState();
    const errors: SkuError[] = [];

    if (!templateConfig) return errors;

    const sku = templateConfig.skus?.[skuId];
    if (!sku) return errors;

    if (sku.category === "static") {
      return errors;
    }

    const skinExists = parsedSkins.some((s) => s.skuId === skuId);
    if (!skinExists) {
      errors.push({
        type: "orphan",
        message: "Skin 已不存在",
      });
    }

    const toningIds = sku.toningIds || [];
    if (toningIds.length > 0) {
      const validToningIds = new Set(
        (templateConfig.tonings || []).map((t: any) => t.id),
      );
      const invalidIds = toningIds.filter(
        (id: string) => !validToningIds.has(id),
      );
      if (invalidIds.length > 0) {
        errors.push({
          type: "invalid_toning",
          message: `${invalidIds.length} 个调色引用不存在`,
          details: invalidIds,
        });
      }

      const uniqueIds = new Set(toningIds);
      if (uniqueIds.size !== toningIds.length) {
        errors.push({
          type: "duplicate_toning",
          message: `有重复的调色绑定 (${toningIds.length} 条，实际 ${uniqueIds.size} 个)`,
        });
      }
    }
    return errors;
  },

  cleanSkuToning: (skuId) => {
    const { templateConfig } = useConfigStore.getState();
    if (!templateConfig) return;

    const sku = templateConfig.skus?.[skuId];
    if (!sku || !sku.toningIds || sku.toningIds.length === 0) return;

    const validToningIds = new Set(
      (templateConfig.tonings || []).map((t: any) => t.id),
    );

    const cleanedIds = [...new Set(sku.toningIds)].filter((id: string) =>
      validToningIds.has(id),
    );

    if (
      cleanedIds.length === sku.toningIds.length &&
      cleanedIds.every((id, i) => id === sku.toningIds[i])
    ) {
      return;
    }

    const skus = { ...templateConfig.skus };
    skus[skuId] = {
      ...sku,
      toningIds: cleanedIds.length > 0 ? cleanedIds : undefined,
    };

    useConfigStore.setState({ templateConfig: { ...templateConfig, skus } });
    set({ isDirty: true });
  },

  updateSkuPro: (skinName, pro) => {
    const { templateConfig, parsedSkins } = useConfigStore.getState();
    if (!templateConfig) return;

    const parsedSkin = parsedSkins.find((s) => s.name === skinName);
    if (!parsedSkin) return;
    const skuId = parsedSkin.skuId;
    const skus = templateConfig.skus || {};
    const currentSku = skus[skuId] || {};

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        skus: {
          ...skus,
          [skuId]: {
            ...currentSku,
            id: skuId,
            skinName,
            category: parsedSkin.category,
            pro,
          },
        },
      },
    });
    set({ isDirty: true });
  },

  updateSkuHidden: (skinName, hidden) => {
    const { templateConfig, parsedSkins } = useConfigStore.getState();
    if (!templateConfig) return;

    const parsedSkin = parsedSkins.find((s) => s.name === skinName);
    if (!parsedSkin) return;
    const skuId = parsedSkin.skuId;
    const skus = templateConfig.skus || {};
    const currentSku = skus[skuId] || {};

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        skus: {
          ...skus,
          [skuId]: {
            ...currentSku,
            id: skuId,
            skinName,
            category: parsedSkin.category,
            hidden: hidden || undefined,
          },
        },
      },
    });
    set({ isDirty: true });
  },

  addSkuToning: (skinName, toningId) => {
    const { templateConfig, parsedSkins } = useConfigStore.getState();
    if (!templateConfig) return;

    const parsedSkin = parsedSkins.find((s) => s.name === skinName);
    if (!parsedSkin) return;
    const skuId = parsedSkin.skuId;
    const skus = templateConfig.skus || {};
    const currentSku = skus[skuId] || {};
    const currentIds = currentSku.toningIds || [];

    if (currentIds.includes(toningId)) return;

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        skus: {
          ...skus,
          [skuId]: {
            ...currentSku,
            id: skuId,
            skinName,
            category: parsedSkin.category,
            toningIds: [...currentIds, toningId],
          },
        },
      },
    });
    set({ isDirty: true });
  },

  removeSkuToning: (skinName, toningId) => {
    const { templateConfig, parsedSkins } = useConfigStore.getState();
    if (!templateConfig) return;

    const parsedSkin = parsedSkins.find((s) => s.name === skinName);
    if (!parsedSkin) return;
    const skuId = parsedSkin.skuId;
    const skus = templateConfig.skus || {};
    const currentSku = skus[skuId];

    if (!currentSku?.toningIds) return;

    const newIds = currentSku.toningIds.filter((id: string) => id !== toningId);

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        skus: {
          ...skus,
          [skuId]: {
            ...currentSku,
            toningIds: newIds.length > 0 ? newIds : undefined,
          },
        },
      },
    });
    set({ isDirty: true });
  },

  setSkuTonings: (skinName, toningIds) => {
    const { templateConfig, parsedSkins } = useConfigStore.getState();
    if (!templateConfig) return;

    const parsedSkin = parsedSkins.find((s) => s.name === skinName);
    if (!parsedSkin) return;
    const skuId = parsedSkin.skuId;
    const skus = templateConfig.skus || {};
    const currentSku = skus[skuId] || {};

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        skus: {
          ...skus,
          [skuId]: {
            ...currentSku,
            id: skuId,
            skinName,
            category: parsedSkin.category,
            toningIds: toningIds.length > 0 ? toningIds : undefined,
          },
        },
      },
    });
    set({ isDirty: true });
  },

  // ============ Selection Management ============

  addSkuToSelection: (selectionId, skinName) => {
    const { templateConfig, parsedSkins } = useConfigStore.getState();
    if (!templateConfig) return;

    const skus = templateConfig.skus || {};

    const parsedSkin = parsedSkins.find((s) => s.name === skinName);
    if (!parsedSkin) {
      console.error("Skin not found in parsedSkins:", skinName);
      return;
    }

    const skuId = parsedSkin.skuId;

    for (const sel of templateConfig.selection) {
      const selSkuIds = sel.skuIds || sel.sku?.map((s: any) => s.id) || [];
      if (selSkuIds.includes(skuId)) {
        console.warn("SKU already exists in selection:", sel.id);
        return;
      }
    }

    const newSku: SKU = {
      id: skuId,
      skinName: skinName,
      category: parsedSkin.category,
      pro: skus[skuId]?.pro || 0,
      toningIds: skus[skuId]?.toningIds,
      hidden: skus[skuId]?.hidden,
    };

    const targetSel = templateConfig.selection.find(
      (sel) => sel.id === selectionId,
    );
    const nextSkus = { ...skus, [skuId]: newSku };
    // 层已必选时，新加 SKU 的 category 也写入 requireCategories
    const nextRequireCategories =
      targetSel?.opt === false && parsedSkin.category !== "static"
        ? syncRequireCategoriesForSelection(
            templateConfig.requireCategories,
            [parsedSkin.category],
            true,
          )
        : templateConfig.requireCategories || [];

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        skus: nextSkus,
        requireCategories: nextRequireCategories,
        selection: templateConfig.selection.map((sel) =>
          sel.id === selectionId
            ? {
                ...sel,
                skuIds: [
                  ...(sel.skuIds || sel.sku?.map((s: any) => s.id) || []),
                  skuId,
                ],
              }
            : sel,
        ),
      },
    });
    set({ isDirty: true });
  },

  addStaticSkuToSelection: (selectionId) => {
    const { templateConfig } = useConfigStore.getState();
    if (!templateConfig) return undefined;

    const skus = { ...(templateConfig.skus || {}) };
    // 与 parseSkins 一致：8 位 base36（generateSkuId），避免与现有 sku 键冲突则重试
    let skuId = "";
    for (let n = 0; n < 64; n++) {
      const candidate = generateSkuId(
        `__static__${Date.now()}_${n}_${Math.random()}`,
      );
      if (!skus[candidate]) {
        skuId = candidate;
        break;
      }
    }
    if (!skuId) return undefined;

    const newSku: SKU = {
      id: skuId,
      // 每条静态 SKU 唯一 skinName，避免与同名冲突
      skinName: `static/${skuId}`,
      category: "static",
      pro: 0,
    };

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        skus: {
          ...skus,
          [skuId]: newSku,
        },
        selection: templateConfig.selection.map((sel) =>
          sel.id === selectionId
            ? {
                ...sel,
                skuIds: [...(sel.skuIds || sel.sku?.map((s: any) => s.id) || []), skuId],
              }
            : sel,
        ),
      },
    });
    set({ isDirty: true });
    return skuId;
  },

  addStaticSkuToSelectionWithAssets: (selectionId, assets) => {
    const { templateConfig } = useConfigStore.getState();
    if (!templateConfig) return undefined;

    const skus = { ...(templateConfig.skus || {}) };
    let skuId = "";
    for (let n = 0; n < 64; n++) {
      const candidate = generateSkuId(
        `__static__${Date.now()}_${n}_${Math.random()}`,
      );
      if (!skus[candidate]) {
        skuId = candidate;
        break;
      }
    }
    if (!skuId) return undefined;

    const newSku: SKU = {
      id: skuId,
      skinName: `static/${skuId}`,
      category: "static",
      pro: 0,
      originalAssetId: assets.originalAssetId,
      thumbAssetId: assets.thumbAssetId,
    };

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        skus: {
          ...skus,
          [skuId]: newSku,
        },
        selection: templateConfig.selection.map((sel) =>
          sel.id === selectionId
            ? {
                ...sel,
                skuIds: [...(sel.skuIds || sel.sku?.map((s: any) => s.id) || []), skuId],
              }
            : sel,
        ),
      },
    });
    set({ isDirty: true });
    return skuId;
  },

  updateStaticSkuField: (skuId, patch) => {
    const { templateConfig } = useConfigStore.getState();
    if (!templateConfig) return;
    const skus = templateConfig.skus || {};
    const cur = skus[skuId];
    if (!cur || cur.category !== "static") return;

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        skus: {
          ...skus,
          [skuId]: { ...cur, ...patch, id: skuId },
        },
      },
    });
    set({ isDirty: true });
  },

  updateSkuField: (skuId, patch) => {
    const { templateConfig } = useConfigStore.getState();
    if (!templateConfig) return;
    const skus = templateConfig.skus || {};
    const cur = skus[skuId];
    if (!cur) return;

    const { thumbAssetId, originalAssetId, ...rest } = patch;
    const next: SKU = { ...cur, ...rest, id: skuId };

    if (thumbAssetId === "" || thumbAssetId === null) {
      delete next.thumbAssetId;
    } else if (typeof thumbAssetId === "string") {
      next.thumbAssetId = thumbAssetId;
    }

    if (originalAssetId === "" || originalAssetId === null) {
      delete next.originalAssetId;
    } else if (typeof originalAssetId === "string") {
      next.originalAssetId = originalAssetId;
    }

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        skus: {
          ...skus,
          [skuId]: next,
        },
      },
    });
    set({ isDirty: true });
  },

  removeStaticSkuFromSelection: (selectionId, skuId) => {
    const { templateConfig } = useConfigStore.getState();
    if (!templateConfig) return;

    const selection = templateConfig.selection.map((sel) => {
      if (sel.id !== selectionId) return sel;
      const currentSkuIds = sel.skuIds || sel.sku?.map((s: any) => s.id) || [];
      return {
        ...sel,
        skuIds: currentSkuIds.filter((id: string) => id !== skuId),
      };
    });

    const stillUsed = selection.some((sel) =>
      (sel.skuIds || []).includes(skuId),
    );

    const skus = { ...(templateConfig.skus || {}) };
    if (!stillUsed && skus[skuId]?.category === "static") {
      delete skus[skuId];
    }

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        selection,
        skus,
      },
    });
    set({ isDirty: true });
  },

  addMultipleSkusToSelection: (selectionId, skinNames) => {
    const { templateConfig, parsedSkins } = useConfigStore.getState();
    if (!templateConfig) return;

    const skus = { ...(templateConfig.skus || {}) };

    const skinMap = new Map<string, any>();
    for (const skin of parsedSkins) {
      skinMap.set(skin.name, skin);
    }

    const existingSkuIds = new Set<string>();
    for (const sel of templateConfig.selection) {
      const selSkuIds = sel.skuIds || sel.sku?.map((s: any) => s.id) || [];
      for (const skuId of selSkuIds) {
        existingSkuIds.add(skuId);
      }
    }

    const newSkuIds: string[] = [];
    for (const skinName of skinNames) {
      const parsedSkin = skinMap.get(skinName);
      if (!parsedSkin) continue;

      const skuId = parsedSkin.skuId;
      if (existingSkuIds.has(skuId)) continue;

      skus[skuId] = {
        id: skuId,
        skinName: skinName,
        category: parsedSkin.category,
        pro: skus[skuId]?.pro || 0,
        toningIds: skus[skuId]?.toningIds,
        hidden: skus[skuId]?.hidden,
      };
      newSkuIds.push(skuId);
    }

    if (newSkuIds.length === 0) return;

    const targetSel = templateConfig.selection.find(
      (sel) => sel.id === selectionId,
    );
    let nextRequireCategories = templateConfig.requireCategories || [];
    if (targetSel?.opt === false) {
      const cats = newSkuIds
        .map((id) => skus[id]?.category)
        .filter((c): c is string => !!c && c !== "static");
      nextRequireCategories = syncRequireCategoriesForSelection(
        nextRequireCategories,
        cats,
        true,
      );
    }

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        skus,
        requireCategories: nextRequireCategories,
        selection: templateConfig.selection.map((sel) =>
          sel.id === selectionId
            ? {
                ...sel,
                skuIds: [
                  ...(sel.skuIds || sel.sku?.map((s: any) => s.id) || []),
                  ...newSkuIds,
                ],
              }
            : sel,
        ),
      },
    });
    set({ isDirty: true });
  },

  removeSkuFromSelection: (selectionId, skuId) => {
    const { templateConfig } = useConfigStore.getState();
    if (!templateConfig) return;

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        selection: templateConfig.selection.map((sel) => {
          if (sel.id !== selectionId) return sel;
          const currentSkuIds =
            sel.skuIds || sel.sku?.map((s: any) => s.id) || [];
          return {
            ...sel,
            skuIds: currentSkuIds.filter((id: string) => id !== skuId),
          };
        }),
      },
    });
    set({ isDirty: true });
  },

  removeOrphanSkus: () => {
    const { parsedSkins, templateConfig } = useConfigStore.getState();
    if (!templateConfig) return 0;

    const validSkuIds = new Set<string>(parsedSkins.map((s) => s.skuId));
    let removedCount = 0;

    const newSelection = templateConfig.selection.map((sel) => {
      const currentSkuIds = sel.skuIds || sel.sku?.map((s: any) => s.id) || [];
      const validSkuIdsList = currentSkuIds.filter((skuId: string) => {
        const row = templateConfig.skus?.[skuId];
        if (row?.category === "static") return true;
        const isValid = validSkuIds.has(skuId);
        if (!isValid) removedCount++;
        return isValid;
      });
      return { ...sel, skuIds: validSkuIdsList };
    });

    const skus = { ...(templateConfig.skus || {}) };
    for (const skuId of Object.keys(skus)) {
      if (skus[skuId]?.category === "static") continue;
      if (!validSkuIds.has(skuId)) {
        delete skus[skuId];
      }
    }

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        skus,
        selection: newSelection,
      },
    });
    set({ isDirty: true });

    return removedCount;
  },

  bindToningToSkus: (toningId, skuIds) => {
    const { templateConfig } = useConfigStore.getState();
    if (!templateConfig) return;

    const skuIdsToUpdate = new Set(skuIds.map((s) => s.skuId));
    const skus = { ...(templateConfig.skus || {}) };

    for (const skuId of Array.from(skuIdsToUpdate)) {
      const currentSku = skus[skuId as string];
      if (!currentSku) continue;

      const currentIds = currentSku.toningIds || [];
      if (!currentIds.includes(toningId)) {
        skus[skuId as string] = {
          ...currentSku,
          toningIds: [...currentIds, toningId],
        };
      }
    }

    useConfigStore.setState({
      templateConfig: { ...templateConfig, skus },
    });
    set({ isDirty: true });
  },

  unbindToningFromSkus: (toningId, skuIds) => {
    const { templateConfig } = useConfigStore.getState();
    if (!templateConfig) return;

    const skuIdsToUpdate = new Set(skuIds.map((s) => s.skuId));
    const skus = { ...(templateConfig.skus || {}) };

    for (const skuId of Array.from(skuIdsToUpdate)) {
      const currentSku = skus[skuId as string];
      if (!currentSku?.toningIds) continue;
      const newIds = currentSku.toningIds.filter(
        (id: string) => id !== toningId,
      );
      skus[skuId as string] = {
        ...currentSku,
        toningIds: newIds.length > 0 ? newIds : undefined,
      };
    }

    useConfigStore.setState({
      templateConfig: { ...templateConfig, skus },
    });
    set({ isDirty: true });
  },

  reorderSkuInSelection: (selectionId, oldIndex, newIndex) => {
    const { templateConfig } = useConfigStore.getState();
    if (!templateConfig) return;
    if (oldIndex < 0 || newIndex < 0) return;

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        selection: templateConfig.selection.map((sel) => {
          if (sel.id !== selectionId) return sel;
          const currentSkuIds =
            sel.skuIds || sel.sku?.map((s: any) => s.id) || [];
          if (
            oldIndex >= currentSkuIds.length ||
            newIndex >= currentSkuIds.length
          )
            return sel;

          const newSkuIds = [...currentSkuIds];
          const [removed] = newSkuIds.splice(oldIndex, 1);
          newSkuIds.splice(newIndex, 0, removed);

          return { ...sel, skuIds: newSkuIds };
        }),
      },
    });
    set({ isDirty: true });
  },

  // ============ Toning Actions ============

  addToning: (toning) => {
    const { templateConfig } = useConfigStore.getState();
    if (!templateConfig) return;

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        tonings: [...templateConfig.tonings, toning],
      },
    });
    set({ isDirty: true });
  },

  updateToning: (toningId, updates) => {
    const { templateConfig } = useConfigStore.getState();
    if (!templateConfig) return;

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        tonings: templateConfig.tonings.map((t) =>
          t.id === toningId ? { ...t, ...updates } : t,
        ),
      },
    });
    set({ isDirty: true });
  },

  deleteToning: (toningId) => {
    const { templateConfig } = useConfigStore.getState();
    if (!templateConfig) return;

    const skus = { ...(templateConfig.skus || {}) };
    for (const [skuId, config] of Object.entries(skus)) {
      if ((config as any).toningIds?.includes(toningId)) {
        const newIds = (config as any).toningIds.filter(
          (id: string) => id !== toningId,
        );
        skus[skuId] = {
          ...(config as any),
          toningIds: newIds.length > 0 ? newIds : undefined,
        };
      }
    }

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        tonings: templateConfig.tonings.filter((t) => t.id !== toningId),
        skus,
      },
    });
    set({ isDirty: true });
  },

  addColorSet: (toningId, colorSet) => {
    const { templateConfig } = useConfigStore.getState();
    if (!templateConfig) return;

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        tonings: templateConfig.tonings.map((t) =>
          t.id === toningId ? { ...t, colors: [...t.colors, colorSet] } : t,
        ),
      },
    });
    set({ isDirty: true });
  },

  updateColorSet: (toningId, colorSetId, updates) => {
    const { templateConfig } = useConfigStore.getState();
    if (!templateConfig) return;

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        tonings: templateConfig.tonings.map((t) =>
          t.id === toningId
            ? {
                ...t,
                colors: t.colors.map((cs) =>
                  cs.id === colorSetId ? { ...cs, ...updates } : cs,
                ),
              }
            : t,
        ),
      },
    });
    set({ isDirty: true });
  },

  deleteColorSet: (toningId, colorSetId) => {
    const { templateConfig } = useConfigStore.getState();
    if (!templateConfig) return;

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        tonings: templateConfig.tonings.map((t) =>
          t.id === toningId
            ? {
                ...t,
                colors: t.colors.filter((cs) => cs.id !== colorSetId),
              }
            : t,
        ),
      },
    });
    set({ isDirty: true });
  },

  moveColorSet: (fromToningId, toToningId, colorSetId) => {
    const { templateConfig } = useConfigStore.getState();
    if (!templateConfig) return;
    if (fromToningId === toToningId) return;

    const fromToning = templateConfig.tonings.find(
      (t: any) => t.id === fromToningId,
    );
    const colorSet = fromToning?.colors.find((cs: any) => cs.id === colorSetId);
    if (!colorSet) return;

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        tonings: templateConfig.tonings.map((t: any) => {
          if (t.id === fromToningId) {
            return {
              ...t,
              colors: t.colors.filter((cs: any) => cs.id !== colorSetId),
            };
          }
          if (t.id === toToningId) {
            return {
              ...t,
              colors: [...t.colors, colorSet],
            };
          }
          return t;
        }),
      },
    });
    set({ isDirty: true });
  },

  updateAnimationPro: (animationId, pro) => {
    const { templateConfig } = useConfigStore.getState();
    if (!templateConfig) return;

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        animations: templateConfig.animations.map((anim) =>
          anim.id === animationId ? { ...anim, pro } : anim,
        ),
      },
    });
    set({ isDirty: true });
  },

  updateAnimationLoop: (animationId, loop) => {
    const { templateConfig } = useConfigStore.getState();
    if (!templateConfig) return;

    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        animations: templateConfig.animations.map((anim) =>
          anim.id === animationId ? { ...anim, loop } : anim,
        ),
      },
    });
    set({ isDirty: true });
  },

  toggleRequireCategory: (category) => {
    const { templateConfig } = useConfigStore.getState();
    if (!templateConfig) return;

    const requireCategories = templateConfig.requireCategories || [];
    const isRequired = requireCategories.includes(category);

    // 仅改 requireCategories，不回写层 opt（层必选只从 Selection / LayerIcon 设置）
    useConfigStore.setState({
      templateConfig: {
        ...templateConfig,
        requireCategories: isRequired
          ? requireCategories.filter((c: string) => c !== category)
          : [...requireCategories, category],
      },
    });
    set({ isDirty: true });
  },

  resetPreview: () =>
    set({
      selectedSkins: [],
      selectedToningColors: {},
      currentAnimation: "",
      previewToningId: null,
    }),
}));
